import React, { useState } from 'react';
import { ForgeUIEmbeddedMacroAnalyticsContext } from '../../analytics/ForgeUIEmbeddedMacroAnalyticsContext';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import { fg } from '@atlaskit/platform-feature-flags';

import { useExtensionListRelay as useExtensionList } from '../../web-client/hooks/useExtensionList';
import { GQLExtensionContextsFilterType } from '../../web-client/graphql/types';
import { Loader } from '../../web-runtime';

import { ForgeUIRenderer } from './main';
import { ExtensionNotInstalled } from './ExtensionNotInstalled';
import type {
	ForgeUIRendererProps,
	CloudOrWorkspaceId,
	ConfluenceCustomUIProps,
	EmbeddedUIKitModalContext,
	EmbeddedMacroParentType,
} from './types';
import type { GQLExtension } from '../../web-client/graphql/types';
import { EmbeddedModalContext } from './EmbeddedModalContext';
import { createMessageForwarder } from '../../custom-ui/bridge/utils/embedded-macros/createMessageForwarder';
import { isCustomUI } from '../..';
import { ReservedHeightWrapper } from './ReservedHeightWrapper';
import { calculateViewportSizeHeight, type ViewportSizeType } from './calculateViewportSizeHeight';

type PostRobotSend = typeof import('@atlassian/bridge-core').postRobotSend;

let cachedPostRobotSend: PostRobotSend | null | undefined;

const getPostRobotSend = (): PostRobotSend | null => {
	if (cachedPostRobotSend !== undefined) cachedPostRobotSend;

	if (typeof window === 'undefined') return null;

	try {
		cachedPostRobotSend = require('@atlassian/bridge-core').postRobotSend;
	} catch (_) {
		cachedPostRobotSend = null;
	}
	return cachedPostRobotSend;
};

type EmbeddedMacroRendererProps = Omit<ForgeUIRendererProps, 'extension'> &
	CloudOrWorkspaceId &
	ConfluenceCustomUIProps & {
		createGetContextToken?: (extension: GQLExtension) => () => Promise<string>;
		embeddedMacroParentType?: EmbeddedMacroParentType;
		extensionId: string;
		isInCustomUI?: boolean;
		isInline?: boolean;
	};

type MacroExtensionContainerProps = {
	children: React.ReactNode;
	isInline?: boolean;
};

// Container for supporting inline macros
const MacroExtensionContainer = ({ isInline, children }: MacroExtensionContainerProps) => {
	const inlineEnabled = fg('platform_forge_ui_embedded_inline_macro') && isInline;
	return (
		<div
			data-testid="ForgeExtensionContainer"
			style={{
				display: inlineEnabled ? 'inline-block' : 'block',
				verticalAlign: inlineEnabled ? 'middle' : '',
			}}
		>
			{children}
		</div>
	);
};

const getLoadingComponent = (loadingComponent?: React.ReactNode) => {
	if (loadingComponent) {
		return <>{loadingComponent}</>;
	}
	return <Loader />;
};

const EmbeddedMacroRendererInner = (
	props: EmbeddedMacroRendererProps,
): React.JSX.Element | null => {
	const {
		extensionId,
		contextIds,
		loadingComponent,
		createGetContextToken,
		extensionData,
		embeddedParentLocalId,
		localId,
		extensionPayload,
		isInCustomUI,
		embeddedMacroParentType,
		entryPoint,
		customBridge,
		...rest
	} = props;
	const [embeddedParentModalKey, setEmbeddedParentModalKey] = useState<string | undefined>(
		undefined,
	);

	const { extensions, loading } = useExtensionList({
		contextIds,
		filter: [
			{
				type: GQLExtensionContextsFilterType.ExtensionType,
				value: ['xen:macro'],
			},
		],
	});

	const extension = extensions?.find((ext) => ext.id === extensionId);
	const queryCompleted = extensions !== undefined;
	const notInstalled =
		queryCompleted && !extension && fg('platform_validate_embedded_macro_installation');

	if (loading || !queryCompleted) {
		return getLoadingComponent(loadingComponent);
	}

	if (notInstalled) {
		return <ExtensionNotInstalled extensionId={extensionId} />;
	}

	if (!extension) {
		return null;
	}

	type ForgeUIRendererPropsWithExtension = React.ComponentProps<typeof ForgeUIRenderer>;

	let getContextToken = props.getContextToken;

	// createGetContextToken is the FAUS version of useGetContextToken — it is used for embedded
	// macros with a Custom UI bodied parent.
	if (createGetContextToken) {
		getContextToken = createGetContextToken(extension);
	} else if (getContextToken && fg('forge-ui-bridge-core-reset')) {
		// For embedded macros with a UI Kit parent, getContextToken is
		// passed in by Confluence rather than constructed here.
		// We inject the embedded macro's
		// contextIds, extension, and localId — so bridge calls that uses getContextToken(invoke,
		// realtime, etc.) use the embedded macro's context, not the parent's.
		const innerGetContextToken = getContextToken;
		getContextToken = (options) =>
			innerGetContextToken({
				contextIdsOverride: options?.contextIdsOverride ?? contextIds,
				extensionOverride: { extension, localId },
			});
	}
	const isEmbeddedUIKitInCustomUI = fg('platform_forge_ui_refactor_macro_parent_type')
		? embeddedMacroParentType === 'CustomUI' && !isCustomUI(extension, entryPoint)
		: isInCustomUI && !isCustomUI(extension, entryPoint);
	const embeddedMacroType = isCustomUI(extension, entryPoint) ? 'CustomUI' : 'UIKit';
	const resolvedEmbeddedMacroParentType: EmbeddedMacroParentType =
		embeddedMacroParentType ?? (isInCustomUI ? 'CustomUI' : 'UIKit');
	const contentId = (extensionData?.content as { id?: string })?.id;

	const forgeRendererProps: ForgeUIRendererPropsWithExtension = {
		...rest,
		customBridge,
		contextIds,
		extension,
		loadingComponent,
		getContextToken,
		extensionData,
		embeddedParentLocalId,
		localId,
		extensionPayload,
		entryPoint,
		isEmbeddedUIKitInCustomUI,
		isEmbeddedMacro: !fg('platform_forge_ui_refactor_macro_parent_type'),
		embeddedMacroParentType: fg('platform_forge_ui_refactor_macro_parent_type')
			? resolvedEmbeddedMacroParentType
			: undefined,
		height:
			extension.properties?.viewportSize && fg('platform_forge_ui_embedded_inline_macro')
				? calculateViewportSizeHeight(extension.properties.viewportSize as ViewportSizeType)
				: undefined,
	};

	if (isEmbeddedUIKitInCustomUI && fg('platform_forge_ui_uikit_modal_in_custom_ui_parent')) {
		const postRobotSend = getPostRobotSend(); // This prop is used to determine if the embedded macro is rendered within a Custom UI macro. Since the EmbeddedMacroRenderer is only used for rendering macros within Custom UI, we can set this to true.
		const uiKitModalForwarder =
			fg('forge-ui-bridge-core-reset') && customBridge?.['setEmbeddedMacroModalForgeDoc'];
		const forwarder = uiKitModalForwarder
			? ({ data }: { data: { embeddedUIKitModalContext: EmbeddedUIKitModalContext } }) =>
					uiKitModalForwarder(undefined, data)
			: createMessageForwarder(
					'setEmbeddedMacroModalForgeDoc',
					{
						embeddedMacroExtensionDetails: undefined,
						embeddedMacroLocalId: localId,
						extension,
						extensionData,
						extensionPayload,
						isEmbeddedMacroModal: true,
						sourceAppId: extension?.appId ?? '',
						sourceEnvironmentId: extension?.environmentKey,
						sourceInstallationId: extension?.installationId,
					},
					postRobotSend,
					embeddedParentLocalId,
					{
						appId: extension?.appId ?? '',
						id: extension?.id ?? '',
						installationId: extension?.installationId,
					},
				);
		return (
			<ReservedHeightWrapper extension={extension}>
				<ForgeUIEmbeddedMacroAnalyticsContext
					contentId={contentId}
					embeddedMacroType={embeddedMacroType}
					embeddedMacroParentType={resolvedEmbeddedMacroParentType}
				>
					<EmbeddedModalContext.Provider
						value={{
							embeddedParentModalKey,
							setEmbeddedParentModalKey,
							forwarder,
						}}
					>
						<ForgeUIRenderer {...forgeRendererProps} />
					</EmbeddedModalContext.Provider>
				</ForgeUIEmbeddedMacroAnalyticsContext>
			</ReservedHeightWrapper>
		);
	}
	return (
		<ReservedHeightWrapper extension={extension}>
			<ForgeUIEmbeddedMacroAnalyticsContext
				contentId={contentId}
				embeddedMacroType={embeddedMacroType}
				embeddedMacroParentType={resolvedEmbeddedMacroParentType}
			>
				<ForgeUIRenderer {...forgeRendererProps} />
			</ForgeUIEmbeddedMacroAnalyticsContext>
		</ReservedHeightWrapper>
	);
};

// This wrapper is required to use the Relay environment provider for the useExtensionListRelay hook.
// Once Confluence has fully migrated to Relay, we can remove this wrapper and defer to their Relay environment
export const EmbeddedMacroRenderer = (props: EmbeddedMacroRendererProps): React.JSX.Element => {
	return (
		<AtlassianRelayEnvironmentProvider>
			<MacroExtensionContainer isInline={props.isInline}>
				<EmbeddedMacroRendererInner {...props} />
			</MacroExtensionContainer>
		</AtlassianRelayEnvironmentProvider>
	);
};
